/*
 * The `/pebbl/feedback` API only stores a free-text `message` — it keeps no link to
 * the conversation or the rated Q&A. To still show that context in the review section,
 * the chat embeds it into the message at submit time (as a delimited JSON footer) and
 * the review section parses it back out. Keep these two helpers in sync.
 */

/** Conversation + Q&A context captured when a thumbs-down is submitted. */
export interface FeedbackContext {
  conversationId?: string;
  question?: string;
  answer?: string;
}

/** Marker separating the user's note from the embedded JSON context footer. */
const MARKER = '@@PEBBL_CONTEXT@@';
/** The API caps `message` at 5000 chars; stay safely under it. */
const MAX_MESSAGE = 5000;
const MAX_QUESTION = 600;
/** The user's note is kept in full up to this; the answer gets whatever budget is left. */
const MAX_NOTE = 1000;

function truncate(text: string | undefined, max: number): string {
  const t = (text ?? '').trim();
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
}

/** Assemble the note + delimited JSON context footer into one `message` string. */
function assemble(note: string, footer: { c: string; q: string; a: string }): string {
  return `${note}\n\n${MARKER}${JSON.stringify(footer)}`;
}

/**
 * Combine the user's note with the conversation/Q&A context into a single `message`
 * string. The answer is given as much of the 5000-char budget as remains after the
 * note and question — the review section shows the full response rather than the
 * first 1500 chars (the old fixed cap that lopped the tail off longer answers).
 */
export function encodeFeedbackMessage(note: string, ctx: FeedbackContext): string {
  const c = ctx.conversationId ?? '';
  const q = truncate(ctx.question, MAX_QUESTION);
  const safeNote = truncate(note, MAX_NOTE);
  const fullAnswer = (ctx.answer ?? '').trim();

  // Fast path: the whole answer already fits under the message cap.
  if (assemble(safeNote, { c, q, a: fullAnswer }).length <= MAX_MESSAGE) {
    return assemble(safeNote, { c, q, a: fullAnswer });
  }
  // Otherwise fit the largest answer prefix that still fits. The answer's JSON-escaped
  // length isn't known up front (newlines/quotes expand), so binary-search on the
  // assembled length rather than computing a byte budget.
  let lo = 0;
  let hi = fullAnswer.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const candidate = fullAnswer.slice(0, mid) + '…';
    if (assemble(safeNote, { c, q, a: candidate }).length <= MAX_MESSAGE) lo = mid;
    else hi = mid - 1;
  }
  return assemble(safeNote, { c, q, a: fullAnswer.slice(0, lo) + '…' });
}

/** Split a stored feedback message back into its note and (optional) context footer. */
export function decodeFeedbackMessage(message: string): {
  note: string;
  context: FeedbackContext | null;
} {
  const idx = (message ?? '').indexOf(MARKER);
  if (idx === -1) return { note: message ?? '', context: null };
  const note = message.slice(0, idx).trimEnd();
  try {
    const raw = JSON.parse(message.slice(idx + MARKER.length));
    return {
      note,
      context: {
        conversationId: raw?.c || undefined,
        question: raw?.q || undefined,
        answer: raw?.a || undefined,
      },
    };
  } catch {
    // Malformed footer — fall back to showing the raw message untouched.
    return { note: message, context: null };
  }
}
